import React from "react"
import { Button, Select } from "@quorumus/plum"
import { useQuery } from "@tanstack/react-query"
import { api } from "@/api"
import { useToggleCopilotChat } from "@/components/CopilotAnalyze/hooks/useToggleCopilotChat"
import { useCopilotContext } from "@/components/CopilotAnalyze/Copilot.context"
import classes from "./PersonalizationProfileSelect.module.css"

interface PersonalizationProfile {
    id: string
    name: string
    organization: number
    reference_urls: string[]
    custom_instructions_bill_summary: string
    profile_preview: string
    created_at: string
    updated_at: string
}

interface PersonalizationProfileAPI {
    count: number
    profiles: PersonalizationProfile[]
}

interface PersonalizationProfileSelectProps {
    label: string
    placeholder?: string
    value?: string
    onChange?: (value: string) => void
    disabled?: boolean
    openInDrawer?: boolean
    onNewProfile?: () => void
    onProfileCreated?: (profileId: string) => void
    dataPlumId?: string
    newButtonDataPlumId?: string
}

// Fetch function for profiles
const fetchProfiles = async (): Promise<PersonalizationProfile[]> => {
    const response = await api.get("/api/personalization/profiles/")
    const data: PersonalizationProfileAPI = await response.json()

    // Map API response to ProfileOption format
    return data.profiles
}

// Custom hook to fetch profiles
const useProfiles = () =>
    useQuery({
        queryKey: ["tracking-board-personalization-profile-options"],
        queryFn: fetchProfiles,
        staleTime: 5 * 60 * 1000, // 5 minutes
        refetchOnWindowFocus: false,
    })

export const PersonalizationProfileSelect: React.FC<PersonalizationProfileSelectProps> = ({
    label,
    placeholder = "Choose a personalized profile",
    value,
    onChange,
    disabled = false,
    openInDrawer,
    onNewProfile,
    onProfileCreated,
    dataPlumId,
    newButtonDataPlumId,
}) => {
    const { toggleChat } = useToggleCopilotChat()
    const context = useCopilotContext()

    // Fetch profiles using react-query
    const { data: profiles = [], isLoading, error } = useProfiles()

    // Handle error state by providing fallback empty array
    const profilesData = error ? [] : profiles

    const handleCreateNewProfile = () => {
        // Set fullscreen mode and open copilot with new personalization entryPoint
        context.setIsFullscreenOpen(true)
        context.setPersonalizationId(undefined)
        // Store the callback in context so Footer component can use it
        context.setOnProfileCreated(() => onProfileCreated)
        toggleChat({
            entryPoint: "personalization",
            forceScreenChange: true,
        })
        onNewProfile?.()
    }

    const handleProfileSelect = (selectedValue: string | null) => {
        const selectedProfile = profilesData.find(
            (profile: PersonalizationProfile) => profile.id.toString() === selectedValue,
        )

        if (selectedProfile && openInDrawer) {
            // Find the selected profile to get its label
            if (selectedProfile) {
                context.setPersonalizationId(selectedProfile.id)

                toggleChat({
                    entryPoint: "personalization",
                    forceScreenChange: true,
                })
            }
        }
        // Call the original onChange if provided
        onChange?.(selectedProfile?.id || "")
    }

    // Determine placeholder text based on state
    const getPlaceholder = () => {
        if (isLoading) return "Loading profiles..."
        if (error) return "Error loading profiles"
        return placeholder
    }

    return (
        <Select
            label={label}
            placeholder={getPlaceholder()}
            value={value ?? ""}
            onChange={handleProfileSelect}
            data={profilesData.map((profile) => ({
                label: profile.name,
                value: profile.id.toString(),
            }))}
            disabled={disabled || isLoading}
            clearable
            data-plum-id={dataPlumId}
            variant="searchable"
        >
            <Select.Footer>
                {(Userdata.is_admin || Userdata.Permissions.qp_ai_profile_management) && (
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={handleCreateNewProfile}
                        className={classes.footerButton}
                        data-plum-id={newButtonDataPlumId ?? (dataPlumId ? `new-${dataPlumId}` : undefined)}
                    >
                        Create new Personalized Profile
                    </Button>
                )}
            </Select.Footer>
        </Select>
    )
}
